import React, { useState, useEffect } from 'react';
import './UserDetailModal.css';
import { fundsAPI } from '../../../../../services/api/fundsAPI';
import { userAPI } from '../../../../../services/api/userAPI';
import { extractBalanceData } from '../../FundsManagement/components/utils';
import notificationService from '../../../../../services/notificationService';
import ConfirmDialog from '../../../../../components/ConfirmDialog';

const defaultEnd = new Date().toISOString().slice(0, 10);
const defaultStart = new Date(Date.now() - 15 * 86400000).toISOString().slice(0, 10);

const fmtCurrency = (v, cur, d = 2) => {
  const n = Number(v || 0);
  return `${cur ? cur + ' ' : ''}${n.toFixed(d)}`;
};

/* ==========================================================================
   Helpers
   ========================================================================== */
const formatDate = (d) => {
  if (!d) return 'N/A';
  return new Date(d).toLocaleString('en-US', { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' });
};
const initials = (n, u) => {
  if (n) return n.split(' ').map(p => p[0]).join('').toUpperCase().slice(0, 2);
  return u ? u.slice(0, 2).toUpperCase() : 'U';
};
const uid = (u) => u?.id || u?.accountId || u?._id;

/* ==========================================================================
   UserDetailModal
   ========================================================================== */
const UserDetailModal = ({ isOpen, user, onClose, onBlockUser, onUnblockUser, onDeleteUser }) => {
  const [deleteOpen, setDeleteOpen] = useState(false);
  const [balance, setBalance] = useState(null);
  const [balanceData, setBalanceData] = useState(null);
  const [loadingBal, setLoadingBal] = useState(false);
  const [copied, setCopied] = useState({});

  // ─── Currency change state ─────────────────────────────────────────
  const [currencies, setCurrencies] = useState([]);
  const [loadingCurrencies, setLoadingCurrencies] = useState(false);
  const [targetCurrency, setTargetCurrency] = useState('');
  const [spreadPercent, setSpreadPercent] = useState('0.5');
  const [validating, setValidating] = useState(false);
  const [changing, setChanging] = useState(false);
  const [validation, setValidation] = useState(null);

  // ─── Edit mode state ───────────────────────────────────────────────
  const [editMode, setEditMode] = useState(false);
  const [editForm, setEditForm] = useState({ email: '', username: '' });
  const [saving, setSaving] = useState(false);

  const [startDate, setStartDate] = useState(defaultStart);
  const [endDate, setEndDate] = useState(defaultEnd);
  const [exportLoading, setExportLoading] = useState(false);
  const [statementError, setStatementError] = useState('');

  const fetchBalance = async () => {
    if (!uid(user)) return notificationService.error('User ID not available');
    setLoadingBal(true);
    try {
      const r = await fundsAPI.getUserBalance(uid(user));
      const d = extractBalanceData(r);
      setBalance(d?.balance || d?.currentBalance || 0);
      setBalanceData(d || {});
    } catch { setBalance('Error'); notificationService.error('Failed to fetch user balance'); }
    finally { setLoadingBal(false); }
  };

  useEffect(() => {
    if (isOpen && user) { 
      setBalance(null); setBalanceData(null); setLoadingBal(false); setCopied({});
      setTargetCurrency(''); setSpreadPercent('0.5'); setValidation(null);
      setEditMode(false);
      setEditForm({ email: user.email || '', username: user.username || '' });
      setStartDate(defaultStart);
      setEndDate(defaultEnd);
      setStatementError('');
      // Auto-fetch balance so currentCurrency shows correctly immediately
      fetchBalance();
    }
  }, [isOpen, user]);

  if (!isOpen || !user) return null;

  const handleBlock = () => user.isBlocked ? onUnblockUser() : onBlockUser();

  const doCopy = (text, key) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopied(p => ({ ...p, [key]: true }));
      setTimeout(() => setCopied(p => ({ ...p, [key]: false })), 1800);
    }).catch(() => notificationService.error('Copy failed'));
  };

  // ─── Currency helpers ──────────────────────────────────────────────
  const currentCurrency = (balanceData?.currency || user?.currency || balanceData?.user?.currency || 'USD').toUpperCase();

  const loadCurrencies = async () => {
    setLoadingCurrencies(true);
    try {
      const r = await fundsAPI.getAvailableCurrencies();
      const list = r?.data?.data?.currencies || r?.data?.currencies || [];
      setCurrencies(list.filter(c => c !== currentCurrency));
      if (!list.length) notificationService.warning('No alternative currencies available');
    } catch {
      notificationService.error('Failed to load available currencies');
    } finally { setLoadingCurrencies(false); }
  };

  const handleValidate = async () => {
    if (!targetCurrency) return notificationService.error('Select a target currency');
    const sp = parseFloat(spreadPercent);
    if (isNaN(sp) || sp < 0 || sp > 100) return notificationService.error('Spread must be 0-100%');
    setValidating(true); setValidation(null);
    try {
      const r = await fundsAPI.validateCurrencyChange(uid(user), targetCurrency, sp);
      const d = r?.data?.data || r?.data || {};
      setValidation(d);
      if (!d.canChange && d.issues?.length) {
        notificationService.warning(d.issues.join('; '));
      }
    } catch (e) {
      notificationService.error(e?.response?.data?.error || e.message || 'Validation failed');
    } finally { setValidating(false); }
  };

  const handleChangeCurrency = async () => {
    const sp = parseFloat(spreadPercent);
    if (isNaN(sp) || sp < 0 || sp > 100) return notificationService.error('Spread must be 0-100%');
    if (!validation?.canChange) {
      return notificationService.error('Currency change is not possible. Fix issues first.');
    }
    setChanging(true);
    try {
      const r = await fundsAPI.changeBaseCurrency(uid(user), targetCurrency, sp);
      const d = r?.data?.data || r?.data || {};
      notificationService.success(
        `Currency changed from ${d.previousCurrency || currentCurrency} to ${d.newCurrency || targetCurrency}. ` +
        `Fee: ${d.feeAmount || 0} ${d.newCurrency || targetCurrency}`
      );
      // Refresh balance
      await fetchBalance();
      setTargetCurrency('');
      setValidation(null);
    } catch (e) {
      notificationService.error(e?.response?.data?.error || e.message || 'Failed to change currency');
    } finally { setChanging(false); }
  };

  // ─── Edit handlers ─────────────────────────────────────────────────
  const handleSaveEdit = async () => {
    if (!uid(user)) return notificationService.error('User ID not available');
    setSaving(true);
    try {
      const updateData = {};
      if (editForm.email !== user.email) updateData.email = editForm.email;
      if (editForm.username !== user.username) updateData.username = editForm.username;
      if (Object.keys(updateData).length === 0) {
        setEditMode(false);
        return;
      }
      await userAPI.updateUser(uid(user), updateData);
      notificationService.success('User updated successfully');
      setEditMode(false);
    } catch (e) {
      notificationService.error(e?.response?.data?.details || e.message || 'Failed to update user');
    } finally { setSaving(false); }
  };

  const handleExport = async (format) => {
    const userId = uid(user);
    if (!userId) return notificationService.error('User ID not available');
    if (!startDate || !endDate) {
      setStatementError('Please select both start and end dates');
      return;
    }
    setStatementError('');
    setExportLoading(true);
    const filename = `statement-${userId}-${startDate}-to-${endDate}`;
    try {
      if (format === 'csv') {
        const res = await fundsAPI.getUserStatement(userId, startDate, endDate);
        const data = res?.data?.data || res?.data;
        if (!data?.entries) {
          throw new Error('No statement data returned');
        }
        const cur = data.currency || 'USD';
        const csvEscape = (v) => `"${String(v || '').replace(/"/g, '""')}"`;
        const lines = [];
        lines.push(['Date', 'Type', 'Description', 'Credit', 'Debit', 'Balance'].map(csvEscape).join(','));
        data.entries.forEach(e => {
          lines.push([
            new Date(e.date).toLocaleDateString('en-US'),
            e.type,
            e.description || '',
            e.credit > 0 ? fmtCurrency(e.credit, e.creditCurrency || cur) : '',
            e.debit > 0 ? fmtCurrency(e.debit, e.debitCurrency || cur) : '',
            fmtCurrency(e.balance, e.balanceCurrency || cur),
          ].map(csvEscape).join(','));
        });
        const blob = new Blob([lines.join('\n')], { type: 'text/csv' });
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${filename}.csv`; a.click();
        URL.revokeObjectURL(url);
      } else {
        const response = await fundsAPI.exportStatement(userId, startDate, endDate, format);
        const blob = response.data || response;
        const url = URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.href = url; a.download = `${filename}.${format}`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
        URL.revokeObjectURL(url);
      }
    } catch (err) {
      const msg = err?.response?.data?.details || err.message || `${format.toUpperCase()} export failed`;
      setStatementError(msg);
      notificationService.error(msg);
    } finally { setExportLoading(false); }
  };

  return (
    <div className="modal-overlay" onClick={onClose}>
      <div className="modal-container" onClick={e => e.stopPropagation()}>

        {/* ── Header ── */}
        <div className="modal-header">
          <h2>User Details</h2>
          <button className="modal-close" onClick={onClose} aria-label="Close">&times;</button>
        </div>

        {/* ── Body ── */}
        <div className="modal-body">

          {/* ================================================================
              IDENTITY ROW
              ================================================================ */}
          <div className="identity-row">
            <div className="identity-avatar">{initials(user.name, user.username)}</div>
            <div className="identity-info">
              <h3 className="identity-name">{user.name || user.username || uid(user) || 'Unknown'}</h3>
              <div className="identity-sub">
                <span className={`badge ${user.isBlocked ? 'blocked' : 'active'}`}>
                  <span className="badge-status-dot"></span>
                  {user.isBlocked ? 'Blocked' : 'Active'}
                </span>
                <span className="badge role">
                  <i className="fas fa-user-tag" style={{ fontSize: 9 }}></i>
                  {user.role || user.type || 'Client'}
                </span>
                {user.email && <span className="identity-email">{user.email}</span>}
              </div>
            </div>
          </div>

          {/* ================================================================
              BALANCE CARD
              ================================================================ */}
          <div className="card balance-card">
            <div className="card-title">Account Balance</div>
            {loadingBal ? (
              <span className="balance-loading">Loading balance...</span>
            ) : balance === 'Error' ? (
              <span className="balance-error">Failed to load balance</span>
            ) : balance !== null ? (
              <div className="balance-row">
                <div className="balance-left">
                  <div className="balance-icon"><i className="fas fa-coins"></i></div>
                  <span className="balance-value">{currentCurrency} {parseFloat(balance || 0).toFixed(2)}</span>
                </div>
              </div>
            ) : (
              <button className="balance-btn" onClick={fetchBalance}>
                <i className="fas fa-wallet"></i> Load Balance
              </button>
            )}
          </div>

          {/* ================================================================
              CURRENCY SETTINGS CARD
              ================================================================ */}
          <div className="card currency-card">
            <div className="card-title">
              <i className="fas fa-exchange-alt" style={{ marginRight: 4 }}></i>Currency Settings
            </div>
            <div className="detail-grid">
              <div className="detail-field">
                <span className="detail-label">Current Currency</span>
                <span className="detail-value">
                  <span className="badge active">{currentCurrency}</span>
                </span>
              </div>
            </div>

            {/* Target currency selector */}
            <div className="currency-form">
              <div className="currency-form-row">
                <div className="currency-field">
                  <label className="detail-label">Target Currency</label>
                  {currencies.length === 0 ? (
                    <button 
                      className="balance-btn" 
                      onClick={loadCurrencies}
                      disabled={loadingCurrencies}
                      style={{ marginTop: 4, fontSize: 12 }}
                    >
                      <i className="fas fa-sync-alt"></i> {loadingCurrencies ? 'Loading...' : 'Load Currencies'}
                    </button>
                  ) : (
                    <select
                      className="currency-select"
                      value={targetCurrency}
                      onChange={e => { setTargetCurrency(e.target.value); setValidation(null); }}
                    >
                      <option value="">-- Select currency --</option>
                      {currencies.map(c => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  )}
                </div>

                <div className="currency-field">
                  <label className="detail-label">Spread (%)</label>
                  <div className="spread-input-wrap">
                    <input
                      type="number"
                      className="spread-input"
                      value={spreadPercent}
                      onChange={e => { setSpreadPercent(e.target.value); setValidation(null); }}
                      min="0"
                      max="100"
                      step="0.01"
                      placeholder="0.5"
                    />
                    <span className="spread-suffix">%</span>
                  </div>
                  <span className="detail-hint">Fee charged to user</span>
                </div>
              </div>

              {/* Validate button */}
              {targetCurrency && (
                <button 
                  className="balance-btn" 
                  onClick={handleValidate}
                  disabled={validating}
                  style={{ marginTop: 8, fontSize: 12 }}
                >
                  <i className={`fas ${validating ? 'fa-spinner fa-spin' : 'fa-search'}`}></i>
                  {validating ? 'Checking...' : 'Validate Change'}
                </button>
              )}

              {/* Validation result preview */}
              {validation && (
                <div className={`validation-preview ${validation.canChange ? 'can-change' : 'cannot-change'}`}>
                  {!validation.canChange && validation.issues && (
                    <div className="validation-issues">
                      <i className="fas fa-exclamation-triangle"></i>
                      <ul>
                        {validation.issues.map((issue, i) => (
                          <li key={i}>{issue}</li>
                        ))}
                      </ul>
                    </div>
                  )}

                  {validation.canChange && (
                    <div className="validation-details">
                      <div className="val-row">
                        <span>Current Balance:</span>
                        <strong>{currentCurrency} {parseFloat(validation.currentBalance || 0).toFixed(2)}</strong>
                      </div>
                      <div className="val-row">
                        <span>Conversion Rate:</span>
                        <strong>{parseFloat(validation.spreadAdjustedRate || 0).toFixed(6)}</strong>
                      </div>
                      <div className="val-row">
                        <span>Estimated New Balance:</span>
                        <strong>{validation.targetCurrency} {parseFloat(validation.estimatedNewBalance || 0).toFixed(2)}</strong>
                      </div>
                      <div className="val-row fee">
                        <span>Fee (spread):</span>
                        <strong className="fee-amount">{validation.targetCurrency} {parseFloat(validation.estimatedFee || 0).toFixed(2)}</strong>
                      </div>

                      <button
                        className="modal-btn"
                        onClick={handleChangeCurrency}
                        disabled={changing}
                        style={{ marginTop: 10, width: '100%', background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none' }}
                      >
                        <i className={`fas ${changing ? 'fa-spinner fa-spin' : 'fa-exchange-alt'}`}></i>
                        {changing ? 'Converting...' : `Change to ${validation.targetCurrency}`}
                      </button>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>

          {/* ================================================================
              IDENTITY CARD
              ================================================================ */}
          <div className="card">
            <div className="card-title">
              <i className="fas fa-id-card" style={{ marginRight: 4 }}></i>Identity
            </div>
            <div className="detail-grid">

              {/* User ID — full width */}
              <div className="detail-field span-2">
                <span className="detail-label"><i className="fas fa-fingerprint"></i>User ID</span>
                <div className="copy-row">
                  <code className="detail-value mono">{uid(user) || 'N/A'}</code>
                  {uid(user) && (
                    <button className={`copy-btn ${copied.id ? 'copied' : ''}`} onClick={() => doCopy(uid(user), 'id')}>
                      <i className={`fas ${copied.id ? 'fa-check' : 'fa-copy'}`}></i>
                      {copied.id ? 'Copied' : 'Copy'}
                    </button>
                  )}
                </div>
              </div>

              {/* Username */}
              <div className="detail-field">
                <span className="detail-label"><i className="fas fa-user"></i>Username</span>
                {editMode ? (
                  <input
                    value={editForm.username}
                    onChange={(e) => setEditForm(f => ({ ...f, username: e.target.value }))}
                    className="currency-select"
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0' }}
                  />
                ) : (
                  <span className="detail-value">{user.username || 'N/A'}</span>
                )}
              </div>

              {/* Email */}
              <div className="detail-field">
                <span className="detail-label"><i className="fas fa-envelope"></i>Email</span>
                <div className="copy-row">
                  {editMode ? (
                    <input
                      value={editForm.email}
                      onChange={(e) => setEditForm(f => ({ ...f, email: e.target.value }))}
                      className="currency-select"
                      style={{ width: '100%', padding: '8px 10px', borderRadius: 6, border: '1px solid #e2e8f0' }}
                    />
                  ) : (
                    <>
                      <span className="detail-value">{user.email || 'N/A'}</span>
                      {user.email && (
                        <button className={`copy-btn ${copied.email ? 'copied' : ''}`} onClick={() => doCopy(user.email, 'email')}>
                          <i className={`fas ${copied.email ? 'fa-check' : 'fa-copy'}`}></i>
                        </button>
                      )}
                    </>
                  )}
                </div>
              </div>

              {/* Wallet — full width */}
              <div className="detail-field span-2">
                <span className="detail-label"><i className="fas fa-wallet"></i>Wallet Address</span>
                <div className="copy-row">
                  <code className="detail-value mono">{user.walletAddress || 'N/A'}</code>
                  {user.walletAddress && (
                    <button className={`copy-btn ${copied.wallet ? 'copied' : ''}`} onClick={() => doCopy(user.walletAddress, 'wallet')}>
                      <i className={`fas ${copied.wallet ? 'fa-check' : 'fa-copy'}`}></i>
                    </button>
                  )}
                </div>
              </div>
            </div>
          </div>

          {/* ================================================================
              ACTIVITY CARD
              ================================================================ */}
          <div className="card">
            <div className="card-title">
              <i className="fas fa-chart-line" style={{ marginRight: 4 }}></i>Activity
            </div>
            <div className="detail-grid">
              <div className="detail-field">
                <span className="detail-label"><i className="fas fa-calendar-plus"></i>Registered</span>
                <span className="detail-value">{formatDate(user.createdAt)}</span>
              </div>
              <div className="detail-field">
                <span className="detail-label"><i className="fas fa-clock"></i>Last Active</span>
                <span className="detail-value">{formatDate(user.lastActiveAt || user.sessionTime)}</span>
              </div>
              <div className="detail-field">
                <span className="detail-label"><i className="fas fa-shield-alt"></i>Auth Method</span>
                <span className="detail-value">{user.authMethod || 'Standard'}</span>
              </div>
              <div className="detail-field">
                <span className="detail-label"><i className="fas fa-user-cog"></i>Account Type</span>
                <span className="detail-value">{user.accountType || 'Standard'}</span>
              </div>
            </div>
          </div>

          {/* ================================================================
               STATEMENT CARD
               ================================================================ */}
          <div className="card statement-card">
            <div className="card-title">
              <i className="fas fa-file-invoice" style={{ marginRight: 4 }}></i>Statement
            </div>
            <div className="statement-date-row">
              <div className="statement-date-field">
                <label className="detail-label">From</label>
                <input type="date" value={startDate} onChange={e => setStartDate(e.target.value)} className="currency-select" />
              </div>
              <div className="statement-date-field">
                <label className="detail-label">To</label>
                <input type="date" value={endDate} onChange={e => setEndDate(e.target.value)} className="currency-select" />
              </div>
            </div>
            {statementError && <span className="statement-error">{statementError}</span>}
            <div className="statement-export-row">
              <button
                className="statement-export-btn"
                onClick={() => handleExport('pdf')}
                disabled={exportLoading}
              >
                <i className={`fas ${exportLoading ? 'fa-spinner fa-spin' : 'fa-file-pdf'}`}></i>
                PDF
              </button>
              <button
                className="statement-export-btn excel"
                onClick={() => handleExport('xlsx')}
                disabled={exportLoading}
              >
                <i className={`fas ${exportLoading ? 'fa-spinner fa-spin' : 'fa-file-excel'}`}></i>
                Excel
              </button>
              <button
                className="statement-export-btn csv"
                onClick={() => handleExport('csv')}
                disabled={exportLoading}
              >
                <i className={`fas ${exportLoading ? 'fa-spinner fa-spin' : 'fa-file-csv'}`}></i>
                CSV
              </button>
            </div>
          </div>

          {/* ================================================================
               GROUP CARD (conditional)
               ================================================================ */}
          {user.group && (
            <div className="card">
              <div className="card-title">
                <i className="fas fa-layer-group" style={{ marginRight: 4 }}></i>Group Settings
              </div>
              <div className="detail-grid">
                <div className="detail-field">
                  <span className="detail-label"><i className="fas fa-tag"></i>Group Name</span>
                  <span className="detail-value">{user.group.name}</span>
                </div>
                <div className="detail-field span-2">
                  <span className="detail-label"><i className="fas fa-align-left"></i>Description</span>
                  <span className="detail-value">{user.group.description || 'No description'}</span>
                </div>
                {user.group.leverageSettings && (
                  <div className="detail-field">
                    <span className="detail-label"><i className="fas fa-chart-bar"></i>Max Leverage</span>
                    <span className="detail-value">1:{user.group.leverageSettings.maxLeverage || '100'}</span>
                  </div>
                )}
              </div>
            </div>
          )}

        </div>

        {/* ── Footer ── */}
        <div className="modal-footer">
          {editMode ? (
            <>
              <button className="modal-btn btn-cancel" onClick={() => setEditMode(false)}>Cancel</button>
              <button
                className="modal-btn"
                onClick={handleSaveEdit}
                disabled={saving}
                style={{ background: 'linear-gradient(135deg, #10b981, #059669)', color: '#fff', border: 'none' }}
              >
                <i className={`fas ${saving ? 'fa-spinner fa-spin' : 'fa-save'}`}></i>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </>
          ) : (
            <>
              <button
                className="modal-btn"
                onClick={() => setEditMode(true)}
                style={{ background: 'linear-gradient(135deg, #3b82f6, #2563eb)', color: '#fff', border: 'none' }}
              >
                <i className="fas fa-edit"></i> Edit
              </button>
              <button
                className={`modal-btn ${user.isBlocked ? 'btn-unblock' : 'btn-block'}`}
                onClick={handleBlock}
              >
                <i className={`fas ${user.isBlocked ? 'fa-unlock' : 'fa-ban'}`}></i>
                {user.isBlocked ? 'Unblock User' : 'Block User'}
              </button>
              <button className="modal-btn btn-delete" onClick={() => setDeleteOpen(true)}>
                <i className="fas fa-trash-alt"></i> Delete
              </button>
            </>
          )}
          <button className="modal-btn btn-cancel" onClick={onClose}>
            Close
          </button>
        </div>

      </div>

      <ConfirmDialog
        isOpen={deleteOpen}
        title="Delete User"
        message={`Delete ${user?.name || user?.username || 'this user'}? This cannot be undone.`}
        onConfirm={async () => { const ok = await onDeleteUser(uid(user), 'Admin action'); if (ok) { setDeleteOpen(false); onClose(); } }}
        onCancel={() => setDeleteOpen(false)}
        requireTextConfirmation={true}
        confirmationText="DELETE"
      />
    </div>
  );
};

export default UserDetailModal;
